/**
 * Figures (BAC, PV, EV, AC, …) for a Resource ID subset.
 * The histogram already drops to the checked resources. The analytics cards
 * are job-wide, so an unchecked resource used to leave BAC / CPI unchanged.
 *
 * Hours follow the same week the cards use: the XER Sunday is the remaining
 * week; EV folds anything on or after that Sunday back onto the closed week;
 * PV stops the day before that Sunday; AC drops spent on or after it.
 */

function round2(n) {
  return Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
}

function safeRatio(num, den) {
  const n = Number(num) || 0;
  const d = Number(den) || 0;
  if (d <= 0) return null;
  return round2(n / d);
}

function parseUtc(ymd) {
  const d = new Date(`${String(ymd || '').slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ymdOf(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(ymd, days) {
  const d = parseUtc(ymd);
  if (!d) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return ymdOf(d);
}

export function xerWeekAlignment(dataDate) {
  const d = parseUtc(dataDate);
  if (!d) return { updateWeek: null, closedWeek: null };
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  const updateWeek = ymdOf(d);
  return { updateWeek, closedWeek: addDays(updateWeek, -7) };
}

function resourceId(r) {
  return String(r?.resourceId || r?.id || '');
}

function pointsOf(r, useDaily) {
  if (useDaily && (r?.dailySeries || []).length) return r.dailySeries;
  return r?.series || [];
}

function sumPoints(resources, { through = null, useDaily = false } = {}) {
  let s = 0;
  for (const r of resources || []) {
    for (const p of pointsOf(r, useDaily)) {
      const date = String(p?.date || '').slice(0, 10);
      if (through && date > through) continue;
      s += Number(p?.period) || 0;
    }
  }
  return s;
}

function curveUsesDaily(curve) {
  return Object.values(curve?.byResource || {}).some((r) => (
    (r.dailySeries || []).some((p) => Number(p.period))
  ));
}

function listResources(curve, accept) {
  return Object.values(curve?.byResource || {}).filter((r) => !accept || accept(r));
}

function budgetCurveOf(proposal, weeklyTarget, remaining) {
  const has = (c) => c && Object.keys(c.byResource || {}).length;
  if (has(proposal)) return proposal;
  if (has(weeklyTarget)) return weeklyTarget;
  if (has(remaining)) return remaining;
  return proposal || weeklyTarget || remaining || null;
}

const MAESTRO_ALIAS = {
  MW: 'MWR',
  MI: 'MWR',
  EI: 'ELE',
  EL: 'ELE',
  PI: 'PIP',
  PF: 'PFS',
  IM: 'IN',
  BM: 'BMT',
  ST: 'STR',
  IW: 'STR'
};

function suffixOf(r) {
  const id = resourceId(r).toUpperCase();
  const m = id.match(/-([A-Z0-9-]+)$/);
  if (!m) return '';
  return m[1].replace(/-EX$/, '');
}

function candidateKeys(r) {
  const suffix = suffixOf(r);
  if (!suffix) return [];
  const keys = [suffix];
  const head = suffix.split('-')[0];
  if (MAESTRO_ALIAS[suffix]) keys.push(MAESTRO_ALIAS[suffix]);
  if (MAESTRO_ALIAS[head]) keys.push(MAESTRO_ALIAS[head]);
  return [...new Set(keys)];
}

function claimKey(r, present) {
  const keys = candidateKeys(r).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (present.has(k)) return k;
  }
  return null;
}

function maestroThrough(byDiscipline, key, updateWeek) {
  const weeks = byDiscipline?.[key] || byDiscipline?.[key.toLowerCase()] || {};
  let s = 0;
  for (const [w, h] of Object.entries(weeks)) {
    const week = String(w).slice(0, 10);
    if (updateWeek && week >= updateWeek) continue;
    s += Number(h) || 0;
  }
  return s;
}

/**
 * Maestro spent is stored by discipline, not resource. A resource claims the
 * discipline on its id (26LG0052-MW → MW / MWR). If several resources share
 * that code, spent splits by their budget hours.
 * Returns null when nothing in the file matches a resource id.
 */
function spentForSelection(selected, pool, byDiscipline, updateWeek, universe) {
  const present = new Set(Object.keys(byDiscipline || {}).map((k) => String(k).toUpperCase()));
  if (!present.size) return null;
  const selectedIds = new Set(selected.map(resourceId));
  const owners = universe || pool;
  const ownedOutsidePool = new Set();
  const poolIds = new Set(pool.map(resourceId));
  for (const r of owners) {
    const key = claimKey(r, present);
    if (!key) continue;
    if (!poolIds.has(resourceId(r))) ownedOutsidePool.add(key);
  }
  const groups = new Map();
  let claimed = false;
  for (const r of pool) {
    const key = claimKey(r, present);
    if (!key) continue;
    claimed = true;
    const g = groups.get(key) || { poolHrs: 0, selHrs: 0, poolCount: 0, selCount: 0 };
    const w = Math.abs(sumPoints([r], { useDaily: false }));
    g.poolHrs += w;
    g.poolCount += 1;
    if (selectedIds.has(resourceId(r))) {
      g.selHrs += w;
      g.selCount += 1;
    }
    groups.set(key, g);
  }
  if (!claimed) return null;
  let ac = 0;
  const claimedKeys = new Set(groups.keys());
  for (const [key, g] of groups) {
    const hours = maestroThrough(byDiscipline, key, updateWeek);
    // No budget on the claiming resources: split by headcount so a
    // discipline with spend is not reported as zero cost.
    const share = g.poolHrs > 0
      ? g.selHrs / g.poolHrs
      : (g.poolCount > 0 ? g.selCount / g.poolCount : 0);
    ac += hours * share;
  }
  // Hours whose code nobody claimed stay in when every resource still in
  // the pool is checked. They are not piping just because Piping was turned off.
  const allPoolSelected = pool.every((r) => selectedIds.has(resourceId(r)));
  if (allPoolSelected) {
    for (const key of present) {
      if (claimedKeys.has(key) || ownedOutsidePool.has(key)) continue;
      ac += maestroThrough(byDiscipline, key, updateWeek);
    }
  }
  return ac;
}

export function buildPmiSummary({
  bac, ev, ac, pv, remainingHours = 0, dataDate = null, closedWeek = null, resourceFiltered = false
}) {
  const bacN = round2(bac);
  const evN = round2(ev);
  const acN = round2(ac);
  const pvN = round2(pv);
  const spiSnap = safeRatio(evN, pvN);
  const cpiSnap = safeRatio(evN, acN);
  const facCpi = cpiSnap > 0 ? round2(bacN / cpiSnap) : null;
  const facPlan = round2(acN + (bacN - evN));
  const facCombo = (cpiSnap > 0 && spiSnap > 0)
    ? round2(acN + (bacN - evN) / (cpiSnap * spiSnap))
    : null;
  const fac = facCpi != null ? facCpi : facPlan;
  const etc = fac != null ? round2(fac - acN) : round2(Math.max(bacN - evN, 0));
  const vac = fac != null ? round2(bacN - fac) : null;
  const tcpiBac = (bacN - acN) > 0 ? safeRatio(bacN - evN, bacN - acN) : null;
  const tcpiEac = fac != null && (fac - acN) > 0 ? safeRatio(bacN - evN, fac - acN) : null;
  return {
    bac: bacN,
    pv: pvN,
    ev: evN,
    ac: acN,
    sv: round2(evN - pvN),
    cv: round2(evN - acN),
    spi: spiSnap,
    cpi: cpiSnap,
    fac,
    eac: fac,
    fac_cpi: facCpi,
    fac_plan: facPlan,
    fac_combo: facCombo,
    etc,
    vac,
    tcpi_bac: tcpiBac,
    tcpi_eac: tcpiEac,
    pct_complete: bacN > 0 ? round2((evN / bacN) * 100) : 0,
    pct_spent: bacN > 0 ? round2((acN / bacN) * 100) : 0,
    remaining_hours: round2(remainingHours || Math.max(bacN - evN, 0)),
    data_date: dataDate,
    closed_week: closedWeek || null,
    unit: 'man-hours',
    resource_filtered: resourceFiltered
  };
}

function isExtra(r) {
  if (r?.isExtra) return true;
  return /(?:^|[-_/])ex(?:[-_/]|$)/i.test(resourceId(r));
}

/**
 * @param {object} args
 * @param {string[]|null} args.selectedIds resource ids still checked. Empty = nothing.
 * @param {(r: object) => boolean} [args.accept] discipline / family / shop / scope, without the id check.
 */
export function pmiForResourceSelection({
  proposal,
  weeklyTarget,
  earned,
  remaining,
  actuals,
  selectedIds,
  accept,
  dataDate,
  serverPmi
} = {}) {
  const ids = new Set((selectedIds || []).map(String));
  const chosen = (r) => {
    if (accept && !accept(r)) return false;
    const id = resourceId(r);
    return ids.has(id) || (r?.resourceKey && ids.has(String(r.resourceKey)));
  };
  const pool = (r) => !accept || accept(r);

  const align = xerWeekAlignment(dataDate || serverPmi?.data_date);
  const { updateWeek, closedWeek } = align;
  const budgetCurve = budgetCurveOf(proposal, weeklyTarget, remaining);
  const useDaily = curveUsesDaily(budgetCurve) || curveUsesDaily(earned);
  const pvThrough = !updateWeek
    ? null
    : (useDaily ? addDays(closedWeek, 6) : closedWeek);

  const budgetSelected = listResources(budgetCurve, chosen);
  const budgetIds = new Set(budgetSelected.map(resourceId));
  const extras = listResources(weeklyTarget, (r) => chosen(r) && isExtra(r) && !budgetIds.has(resourceId(r)));
  const earnedSelected = listResources(earned, chosen);
  const remainSelected = listResources(remaining, chosen);

  const bac = sumPoints(budgetSelected, { useDaily }) + sumPoints(extras, { useDaily });
  const pv = sumPoints(budgetSelected, { through: pvThrough, useDaily });
  // Earned on/after the XER Sunday is folded onto the closed week, so the
  // card total is the whole earned curve for these resources.
  const ev = sumPoints(earnedSelected, { useDaily });
  const remainHrs = sumPoints(remainSelected, { useDaily: false });

  const poolResources = listResources(budgetCurve, pool);
  const mappedAc = spentForSelection(
    budgetSelected,
    poolResources,
    actuals?.byDiscipline,
    updateWeek,
    listResources(budgetCurve)
  );
  let ac = mappedAc;
  if (ac == null) {
    const serverAc = Number(serverPmi?.ac) || 0;
    const evAll = sumPoints(listResources(earned, pool), { useDaily });
    ac = evAll > 0 ? serverAc * (ev / evAll) : 0;
  }

  return buildPmiSummary({
    bac,
    ev,
    ac,
    pv,
    remainingHours: remainHrs,
    dataDate: dataDate || serverPmi?.data_date || null,
    closedWeek: closedWeek || serverPmi?.closed_week || null,
    resourceFiltered: true
  });
}
